import {
  AppModal,
  Button,
  Form,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import {
  TaskAssignmentFields,
  type TaskAssignmentSource,
} from "../matter-task-plans/TaskAssignmentFields";
import type { MatterRole, PhaseActionTaskTemplate } from "@/lib/types";
import { MATTER_TASK_PRIORITY_OPTIONS } from "@/lib/data/static/matters/taskStatics";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { z } from "zod";

const dayOffset = z.union([
  z.literal(""),
  z.null(),
  z.coerce.number().int().min(0).max(3650),
]);
const schema = z
  .object({
    title: z.string().trim().min(1, "Title is required").max(100),
    description: z.string().max(5000),
    priority: z.enum(["Low", "Medium", "High", "Urgent"]).optional(),
    startDate: dayOffset,
    dueDate: dayOffset,
    assigneeRoleId: z.string(),
    assigneeUserIds: z.array(z.string()).max(50),
    assigneeBackupUserId: z.string(),
    assignerRoleId: z.string(),
    assignerUserIds: z.array(z.string()).max(50),
    assignerBackupUserId: z.string(),
  })
  .refine(
    (data) =>
      data.startDate === "" ||
      data.startDate === null ||
      data.dueDate === "" ||
      data.dueDate === null ||
      data.startDate < data.dueDate,
    { message: "Start must be before the due date", path: ["startDate"] },
  );
type Values = z.infer<typeof schema>;
const FORM_ID = "phase-action-automatic-task-form";

export function PhaseActionTaskTemplateEditor({
  open,
  onOpenChange,
  template,
  roles,
  onSave,
  onRemove,
  readOnly = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: PhaseActionTaskTemplate;
  roles: MatterRole[];
  onSave: (template: PhaseActionTaskTemplate) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const [assigneeSource, setAssigneeSource] =
    useState<TaskAssignmentSource>("role");
  const [assignerSource, setAssignerSource] =
    useState<TaskAssignmentSource>("role");
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: "",
      assigneeRoleId: "",
      assigneeUserIds: [],
      assigneeBackupUserId: "",
      assignerRoleId: "",
      assignerUserIds: [],
      assignerBackupUserId: "",
      startDate: null,
      dueDate: null,
    },
  });
  const { reset } = form;
  useEffect(() => {
    if (!open) return;
    reset({
      title: template?.title ?? "",
      description: template?.description ?? "",
      priority: template?.priority ?? undefined,
      startDate: template?.startDate ?? null,
      dueDate: template?.dueDate ?? null,
      assigneeRoleId: template?.assigneeRoleId ?? "",
      assigneeUserIds: template?.assigneeUserIds ?? [],
      assigneeBackupUserId: template?.assigneeBackupUserId ?? "",
      assignerRoleId: template?.assignerRoleId ?? "",
      assignerUserIds: template?.assignerUserIds ?? [],
      assignerBackupUserId: template?.assignerBackupUserId ?? "",
    });
    setAssigneeSource(template?.assigneeUserIds.length ? "user" : "role");
    setAssignerSource(template?.assignerUserIds.length ? "user" : "role");
  }, [open, template, reset]);
  const submit = (values: Values) => {
    if (readOnly) return;
    onSave({
      ...values,
      id: template?.id,
      description: values.description || null,
      priority: values.priority ?? null,
      startDate: values.startDate === "" ? null : values.startDate,
      dueDate: values.dueDate === "" ? null : values.dueDate,
      assigneeRoleId:
        assigneeSource === "role" ? values.assigneeRoleId || null : null,
      assigneeUserIds: assigneeSource === "user" ? values.assigneeUserIds : [],
      assigneeBackupUserId:
        assigneeSource === "role" && values.assigneeRoleId
          ? values.assigneeBackupUserId || null
          : null,
      assignerRoleId:
        assignerSource === "role" ? values.assignerRoleId || null : null,
      assignerUserIds: assignerSource === "user" ? values.assignerUserIds : [],
      assignerBackupUserId:
        assignerSource === "role" && values.assignerRoleId
          ? values.assignerBackupUserId || null
          : null,
    });
    onOpenChange(false);
  };
  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={onOpenChange}
        title={
          readOnly
            ? "View automatic task"
            : template
              ? "Edit automatic task"
              : "Add automatic task"
        }
        size="3xl"
        overlayClassName="z-[108]"
        className="z-[109]"
        isDirty={!readOnly && form.formState.isDirty}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            {!readOnly && onRemove ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={onRemove}
              >
                Remove automatic task
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                {readOnly ? "Close" : "Cancel"}
              </Button>
              {!readOnly ? (
                <Button type="submit" form={FORM_ID}>
                  Save task
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        <form id={FORM_ID} onSubmit={form.handleSubmit(submit)}>
          <fieldset
            disabled={readOnly}
            className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2"
          >
            <TextFormField
              label="Task title*"
              register={form.register("title")}
              maxLength={100}
              className="col-span-full"
            />
            <TaskAssignmentFields
              readOnly={readOnly}
              label="Assigner"
              roles={roles}
              source={assignerSource}
              enabled={open}
              selectedUserIds={form.watch("assignerUserIds")}
              backupUserId={form.watch("assignerBackupUserId")}
              roleRegister={form.register("assignerRoleId")}
              usersRegister={form.register("assignerUserIds")}
              backupRegister={form.register("assignerBackupUserId")}
              onSourceChange={(source) => {
                setAssignerSource(source);
                if (source === "role") form.setValue("assignerUserIds", []);
                else {
                  form.setValue("assignerRoleId", "");
                  form.setValue("assignerBackupUserId", "");
                }
              }}
            />
            <TaskAssignmentFields
              readOnly={readOnly}
              label="Assignee"
              roles={roles}
              source={assigneeSource}
              enabled={open}
              selectedUserIds={form.watch("assigneeUserIds")}
              backupUserId={form.watch("assigneeBackupUserId")}
              roleRegister={form.register("assigneeRoleId")}
              usersRegister={form.register("assigneeUserIds")}
              backupRegister={form.register("assigneeBackupUserId")}
              onSourceChange={(source) => {
                setAssigneeSource(source);
                if (source === "role") form.setValue("assigneeUserIds", []);
                else {
                  form.setValue("assigneeRoleId", "");
                  form.setValue("assigneeBackupUserId", "");
                }
              }}
            />
            <SimpleSelectFormField
              label="Priority"
              items={MATTER_TASK_PRIORITY_OPTIONS}
              register={form.register("priority")}
              placeholder="Select priority"
            />
            <TextFormField
              label="Start (days after action is added)"
              type="number"
              min={0}
              max={3650}
              register={form.register("startDate")}
            />
            <TextFormField
              label="Due (days after action is added)"
              type="number"
              min={0}
              max={3650}
              register={form.register("dueDate")}
            />
            <div className="col-span-full">
              <TextAreaFormField
                label="Task description"
                register={form.register("description")}
              />
            </div>
          </fieldset>
        </form>
      </AppModal>
    </Form>
  );
}
